import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { FileUpload } from 'primereact/fileupload';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import PageHeader from '../../components/ui/PageHeader';
import { EmpresaService } from '../../services/EmpresaService';
import { UbigeoService } from '../../services/UbigeoService';
import { environment } from '../util/baseUrl';
import '../Usuario.css';

const Empresa = () => {
    const [isEditing, setIsEditing] = useState(false);
    const originalData = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [empresa, setEmpresa] = useState({
        id: null,
        nombre: '',
        nombreComercial: '',
        ruc: '',
        ubigeo: '',
        departamento: '',
        provincia: '',
        distrito: '',
        direccion: '',
        email: '',
        monedaPrincipalIso: 'PEN',
        monedaSimbolo: 'S/',
        impuesto: 'IGV',
        valorImpuesto: 18,
        logo: null,
        // Sunat config
        tipoStockTienda: 'VIRTUAL'
    });
    const [isLoading, setIsLoading] = useState(false);
    const toast = useRef(null);
    const empresaService = EmpresaService;

    const logoUploadRef = useRef(null);

    const tipoStockOptions = [
        { label: 'Virtual', value: 'VIRTUAL' },
        { label: 'Almacén', value: 'ALMACEN' },
        { label: 'Tienda', value: 'TIENDA' }
    ];

    // Estados Ubigeo
    const [departamentos, setDepartamentos] = useState([]);
    const [provinciasDisponibles, setProvinciasDisponibles] = useState([]);
    const [distritosDisponibles, setDistritosDisponibles] = useState([]);

    const loadData = useCallback(async () => {
        try {
            const data = await empresaService.getEmpresas();
            if (data && data.length > 0) {
                const emp = data[0];
                const newData = {
                    ...emp,
                    valorImpuesto: emp.valorImpuesto || 18,
                    monedaPrincipalIso: emp.monedaPrincipalIso || 'PEN',
                    monedaSimbolo: emp.monedaSimbolo || 'S/',
                    impuesto: emp.impuesto || 'IGV',
                    tipoStockTienda: emp.tipoStockTienda || 'VIRTUAL'
                };
                setEmpresa(newData);
                originalData.current = { ...newData };

                // Load ubigeo dropdowns if department is present
                if (newData.departamento) {
                    const provs = await UbigeoService.getProvincias(newData.departamento);
                    setProvinciasDisponibles(provs.map(p => ({ label: p, value: p })));
                    if (newData.provincia) {
                        const dists = await UbigeoService.getDistritos(newData.departamento, newData.provincia);
                        setDistritosDisponibles(dists.map(d => ({ label: d, value: d })));
                    }
                }
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar los datos de la empresa', life: 3000 });
        }
    }, [empresaService]);

    useEffect(() => {
        loadData();
        // Cargar departamentos al montar el componente
        UbigeoService.getDepartamentos().then(data => setDepartamentos(data.map(d => ({ label: d, value: d }))));
    }, [loadData]);

    const handleInputChange = (e, name) => {
        let val = (e.target && e.target.value) !== undefined ? e.target.value : e.value;

        // Validar RUC: solo números, máximo 11 dígitos
        if (name === 'ruc') {
            val = val.replace(/[^0-9]/g, '').substring(0, 11);
        }

        setEmpresa(prev => ({ ...prev, [name]: val }));
    };

    const buscarRUC = async () => {
        if (!empresa.ruc || empresa.ruc.length !== 11) {
            toast.current.show({ severity: 'warn', summary: 'RUC Inválido', detail: 'El RUC debe tener exactamente 11 dígitos', life: 3000 });
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${environment.backendUrl}/api/empresas/search/ruc/${empresa.ruc}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const apiResponse = await response.json();
                const data = apiResponse.data;

                // Manejar múltiples formatos de respuesta de la API SUNAT
                // Intentar obtener razón social desde diferentes posibles nombres de propiedad
                const razonSocial = data?.razonSocial || data?.nombre || data?.nombreComercial || '';
                const direccion = data?.direccion || data?.domicilio || data?.direccionFiscal || '';

                if (razonSocial && direccion) {
                    setEmpresa(prev => ({
                        ...prev,
                        nombre: razonSocial,
                        direccion: direccion
                    }));
                    toast.current.show({ severity: 'success', summary: 'Éxito', detail: `Datos encontrados: ${razonSocial}`, life: 3000 });
                } else if (razonSocial && !direccion) {
                    // Si tenemos razón social pero no dirección, al menos usarla
                    setEmpresa(prev => ({
                        ...prev,
                        nombre: razonSocial
                    }));
                    toast.current.show({ severity: 'success', summary: 'Parcial', detail: `Razón Social: ${razonSocial}. Ingrese la dirección manualmente`, life: 3000 });
                } else {
                    toast.current.show({ severity: 'info', summary: 'Información', detail: 'RUC validado. Ingrese manualmente la razón social y dirección', life: 3000 });
                }
            } else {
                toast.current.show({ severity: 'warn', summary: 'No Encontrado', detail: 'No se pudieron obtener datos para este RUC', life: 3000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al consultar RUC: ' + error.message, life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    // Search Ubigeo by code
    const buscarUbigeo = async () => {
        if (!empresa.ubigeo || (empresa.ubigeo.length !== 6 && empresa.ubigeo.length !== 4)) {
            toast.current.show({ severity: 'warn', summary: 'Aviso', detail: 'Debe ingresar un código ubigeo válido.', life: 3000 });
            return;
        }
        setIsLoading(true);
        try {
            const data = await UbigeoService.getUbigeo(empresa.ubigeo);
            if (data) {
                setEmpresa(prev => ({
                    ...prev,
                    departamento: data.departamento,
                    provincia: data.provincia,
                    distrito: data.distrito
                }));
                // Pre-load dropdowns
                const provs = await UbigeoService.getProvincias(data.departamento);
                setProvinciasDisponibles(provs.map(p => ({ label: p, value: p })));
                const dists = await UbigeoService.getDistritos(data.departamento, data.provincia);
                setDistritosDisponibles(dists.map(d => ({ label: d, value: d })));
                toast.current.show({ severity: 'success', summary: 'Ubicación Encontrada', detail: `${data.distrito}, ${data.provincia}, ${data.departamento}`, life: 3000 });
            } else {
                toast.current.show({ severity: 'info', summary: 'No Encontrado', detail: 'Código ubigeo no encontrado en base de datos.', life: 3000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error en servidor', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    // Manejador para cambio de Departamento
    const handleDepartamentoChange = (e) => {
        const deptSelected = e.value;
        setEmpresa(prev => ({
            ...prev,
            departamento: deptSelected,
            provincia: '', // Resetear provincia
            distrito: ''   // Resetear distrito
        }));

        if (deptSelected) {
            UbigeoService.getProvincias(deptSelected).then(data => {
                setProvinciasDisponibles(data.map(p => ({ label: p, value: p })));
            });
            setDistritosDisponibles([]);
        } else {
            setProvinciasDisponibles([]);
            setDistritosDisponibles([]);
        }
    };

    // Manejador para cambio de Provincia
    const handleProvinciaChange = (e) => {
        const provSelected = e.value;
        setEmpresa(prev => ({
            ...prev,
            provincia: provSelected,
            distrito: ''
        }));

        if (provSelected) {
            UbigeoService.getDistritos(empresa.departamento, provSelected).then(data => {
                setDistritosDisponibles(data.map(d => ({ label: d, value: d })));
            });
        } else {
            setDistritosDisponibles([]);
        }
    };

    // Manejador para cambio de Distrito
    const handleDistritoChange = (e) => {
        setEmpresa(prev => ({
            ...prev,
            distrito: e.value
        }));
    };

    const saveChanges = async () => {
        if (!empresa.nombre || !empresa.ruc) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'El nombre y RUC son obligatorios', life: 3000 });
            return;
        }

        if (empresa.ruc.length !== 11) {
            toast.current.show({ severity: 'warn', summary: 'RUC Inválido', detail: 'El RUC debe tener exactamente 11 dígitos', life: 3000 });
            return;
        }

        setIsLoading(true);
        try {
            const payload = {
                nombre: empresa.nombre,
                nombreComercial: empresa.nombreComercial || null,
                ruc: empresa.ruc,
                ubigeo: empresa.ubigeo || null,
                departamento: empresa.departamento || null,
                provincia: empresa.provincia || null,
                distrito: empresa.distrito || null,
                direccion: empresa.direccion || null,
                telefono: empresa.telefono || null,
                email: empresa.email || null,
                monedaPrincipalIso: empresa.monedaPrincipalIso || 'PEN',
                monedaSimbolo: empresa.monedaSimbolo || 'S/',
                valorImpuesto: parseFloat(empresa.valorImpuesto) || 18,
                impuesto: empresa.impuesto || 'IGV',
                monedaPrincipalPais: empresa.monedaPrincipalPais || 'PERU',
                comisionCambio: parseFloat(empresa.comisionCambio) || 0,
                tipoStockTienda: empresa.tipoStockTienda || 'VIRTUAL'
            };

            let savedEmpresa;
            if (empresa.id) {
                savedEmpresa = await empresaService.actualizarEmpresa(empresa.id, payload);
            } else {
                savedEmpresa = await empresaService.crearEmpresa(payload);
            }

            setEmpresa(prev => ({ ...prev, id: savedEmpresa.id }));
            toast.current.show({ severity: 'success', summary: 'éxito', detail: 'Configuración guardada correctamente', life: 3000 });
            setIsEditing(false);
            await loadData();
        } catch (error) {
            console.error('Error al guardar:', error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al guardar: ' + (error.response?.data?.message || error.message), life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const onLogoSelect = async (e) => {
        if (e.files && e.files.length > 0 && empresa.id) {
            const file = e.files[0];
            setIsLoading(true);
            try {
                await empresaService.uploadLogo(empresa.id, file);
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Logo actualizado correctamente', life: 3000 });
                await loadData();
                logoUploadRef.current.clear();
            } catch (error) {
                const serverError = error?.response?.data?.error || error?.response?.data?.message;
                toast.current.show({ severity: 'error', summary: 'Error', detail: serverError || 'No se pudo subir el logo', life: 4000 });
            } finally {
                setIsLoading(false);
            }
        } else if (!empresa.id) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Primero debe guardar los datos generales.', life: 3000 });
            logoUploadRef.current.clear();
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        if (originalData.current) {
            setEmpresa({ ...originalData.current });
        }
        setIsEditing(false);
    };

    const onTabChange = (e) => {
        const nextIndex = e.index;
        if (isEditing) {
            const hasChanges = JSON.stringify(empresa) !== JSON.stringify(originalData.current);
            if (hasChanges) {
                confirmDialog({
                    message: 'Tiene cambios sin guardar en su empresa. ¿Desea continuar y perder los cambios?',
                    header: 'Cambios sin guardar',
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: 'Sí, descartar',
                    rejectLabel: 'No, quedarme aquí',
                    accept: () => {
                        handleCancel();
                        setActiveIndex(nextIndex);
                    }
                });
                return;
            }
        }
        setActiveIndex(nextIndex);
    };


    return (
        <div className="grid usuario-page">
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="col-12">
                <PageHeader
                    title="Configuración de la Empresa"
                    description="Administra la configuración general y opciones de tu e-commerce"
                    icon="pi pi-building"
                />



                <Card>

                    <TabView activeIndex={activeIndex} onTabChange={onTabChange}>
                        <TabPanel header="Datos Generales" leftIcon="pi pi-building mr-2">
                            <div className="grid">
                                <div className="col-12 lg:col-8">
                                    <div className="surface-card p-4 shadow-2 border-round-xl mb-4">
                                        <div className="text-xl font-medium text-900 mb-4 border-bottom-1 surface-border pb-3">
                                            <i className="pi pi-id-card text-primary mr-2 text-xl"></i>
                                            Información Principal
                                        </div>
                                        <div className="p-fluid grid">
                                            <div className="col-12 md:col-6 field mb-4">
                                                <label htmlFor="ruc" className="font-medium text-700">RUC <span className="text-red-500">*</span></label>
                                                <div className="p-inputgroup">
                                                    <InputText style={{ paddingLeft: "2.5rem" }} id="ruc" value={empresa.ruc} disabled={!isEditing} onChange={(e) => handleInputChange(e, 'ruc')} maxLength="11" placeholder="Ej: 20123456789" />
                                                    <Button icon="pi pi-search" onClick={buscarRUC} disabled={!isEditing || empresa.ruc.length !== 11 || isLoading} tooltip="Validar en SUNAT" tooltipOptions={{ position: 'top' }} className="p-button-outlined p-button-primary" />
                                                </div>
                                                <small className="text-500 block mt-1">Busque el comprobante de 11 dígitos</small>
                                            </div>
                                            <div className="col-12 md:col-6 field mb-4">
                                                <label htmlFor="nombre" className="font-medium text-700">Razón Social <span className="text-red-500">*</span></label>
                                                <InputText style={{ paddingLeft: "2.5rem" }} id="nombre" value={empresa.nombre} disabled={!isEditing} onChange={(e) => handleInputChange(e, 'nombre')} placeholder="Ej: Mi Empresa S.A.C" />
                                            </div>
                                            <div className="col-12 md:col-6 field mb-4">
                                                <label htmlFor="nombreComercial" className="font-medium text-700">Nombre Comercial</label>
                                                <InputText style={{ paddingLeft: "2.5rem" }} id="nombreComercial" value={empresa.nombreComercial || ''} disabled={!isEditing} onChange={(e) => handleInputChange(e, 'nombreComercial')} placeholder="Ej: Mi Empresa Tienda" />
                                            </div>
                                            <div className="col-12 md:col-6 field mb-4">
                                                <label htmlFor="ubigeo" className="font-medium text-700">Ubigeo</label>
                                                <div className="p-inputgroup">
                                                    <InputText style={{ paddingLeft: "2.5rem" }} id="ubigeo" value={empresa.ubigeo || ''} disabled={!isEditing} onChange={(e) => handleInputChange(e, 'ubigeo')} placeholder="Ej: 150101" />
                                                    <Button icon="pi pi-search" onClick={buscarUbigeo} disabled={!isEditing || !empresa.ubigeo || isLoading} tooltip="Validar Ubigeo" tooltipOptions={{ position: 'top' }} className="p-button-outlined p-button-secondary" />
                                                </div>
                                            </div>
                                            <div className="col-12 md:col-4 field mb-4">
                                                <label htmlFor="departamento" className="font-medium text-700">Departamento</label>
                                                <Dropdown id="departamento" value={empresa.departamento} disabled={!isEditing} options={departamentos} onChange={handleDepartamentoChange} placeholder="Seleccione un departamento" className="w-full" filter />
                                            </div>
                                            <div className="col-12 md:col-4 field mb-4">
                                                <label htmlFor="provincia" className="font-medium text-700">Provincia</label>
                                                <Dropdown id="provincia" value={empresa.provincia} disabled={!isEditing || !empresa.departamento} options={provinciasDisponibles} onChange={handleProvinciaChange} placeholder="Seleccione una provincia" className="w-full" filter />
                                            </div>
                                            <div className="col-12 md:col-4 field mb-4">
                                                <label htmlFor="distrito" className="font-medium text-700">Distrito</label>
                                                <Dropdown id="distrito" value={empresa.distrito} disabled={!isEditing || !empresa.provincia} options={distritosDisponibles} onChange={handleDistritoChange} placeholder="Seleccione un distrito" className="w-full" filter />
                                            </div>
                                            <div className="col-12 field">
                                                <label htmlFor="direccion" className="font-medium text-700">Dirección Fiscal</label>
                                                <InputText style={{ paddingLeft: "2.5rem" }} id="direccion" value={empresa.direccion || ''} disabled={!isEditing} onChange={(e) => handleInputChange(e, 'direccion')} placeholder="Domicilio registrado en SUNAT..." />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="surface-card p-4 shadow-2 border-round-xl">
                                        <div className="text-xl font-medium text-900 mb-4 border-bottom-1 surface-border pb-3">
                                            <i className="pi pi-phone text-primary mr-2 text-xl"></i>
                                            Contacto
                                        </div>
                                        <div className="p-fluid grid">
                                            <div className="col-12 md:col-6 field mb-4">
                                                <label htmlFor="telefono" className="font-medium text-700">Teléfono</label>
                                                <span className="p-input-icon-left w-full">
                                                    <i className="pi pi-phone" />
                                                    <InputText style={{ paddingLeft: "2.5rem" }} id="telefono" value={empresa.telefono || ''} disabled={!isEditing} onChange={(e) => handleInputChange(e, 'telefono')} placeholder="Ej: 01 2345678" />
                                                </span>
                                            </div>
                                            <div className="col-12 md:col-6 field mb-4">
                                                <label htmlFor="email" className="font-medium text-700">Correo Electrónico</label>
                                                <span className="p-input-icon-left w-full">
                                                    <i className="pi pi-envelope" />
                                                    <InputText style={{ paddingLeft: "2.5rem" }} id="email" value={empresa.email || ''} disabled={!isEditing} onChange={(e) => handleInputChange(e, 'email')} placeholder="empresa@dominio.com" />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 lg:col-4">
                                    <div className="surface-card p-4 shadow-2 border-round-xl flex flex-column align-items-center h-full relative overflow-hidden">
                                        <div className="absolute top-0 left-0 w-full h-8rem bg-primary-100 border-round-top-xl" style={{ zIndex: 0 }}></div>

                                        <div className="text-xl font-medium text-900 mb-5 w-full text-center relative z-1" style={{ paddingTop: '0.5rem' }}>
                                            Logotipo Institucional
                                        </div>

                                        <div className="relative z-1 flex align-items-center justify-content-center p-2 mb-4 border-circle surface-0 border-3 border-white shadow-3"
                                            style={{ width: '220px', height: '220px', transition: 'all 0.3s ease' }}>
                                            {empresa.logo ? (
                                                <img src={`${environment.backendUrl}${empresa.logo}`} alt="Logo Empresa" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }} />
                                            ) : (
                                                <div className="flex flex-column align-items-center text-400">
                                                    <i className="pi pi-image text-indigo-200" style={{ fontSize: '4.5rem', marginBottom: '0.5rem' }}></i>
                                                    <span className="text-sm font-medium">Sin logotipo</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="w-full mt-auto relative z-1 pt-3 border-top-1 surface-border">
                                            <FileUpload disabled={!isEditing}
                                                ref={logoUploadRef}
                                                mode="basic"
                                                name="file"
                                                accept="image/*"
                                                maxFileSize={2000000}
                                                customUpload
                                                uploadHandler={onLogoSelect}
                                                auto
                                                chooseLabel="Modificar Imagen"
                                                className="p-button-outlined w-full p-button-rounded"
                                            />
                                            <p className="text-center text-500 mt-3 mb-0 text-sm line-height-2">
                                                Formatos: JPG, PNG, WEBP. <br />Tamaño máximo: 2MB.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </TabPanel>

                        <TabPanel header="Configuración Financiera" leftIcon="pi pi-wallet text-green-500 mr-2">
                            <div className="grid">
                                <div className="col-12 lg:col-6">
                                    <div className="surface-card p-4 shadow-2 border-round-xl h-full line-height-3">
                                        <div className="text-xl font-medium text-900 mb-4 border-bottom-1 surface-border pb-3">
                                            <i className="pi pi-money-bill text-primary mr-2"></i>
                                            Impuestos y Moneda Base
                                        </div>

                                        <p className="text-600 mb-5">
                                            Establezca las opciones de facturación, cálculo de impuestos y unidad monetaria predeterminada.
                                        </p>

                                        <div className="p-fluid grid">
                                            <div className="col-12 md:col-4 field mb-4">
                                                <label htmlFor="monedaPrincipalIso" className="font-medium text-700">Tipo de Moneda (ISO) <span className="text-red-500">*</span></label>
                                                <span className="p-input-icon-left w-full">
                                                    <i className="pi pi-globe" />
                                                    <InputText style={{ paddingLeft: "2.5rem" }} id="monedaPrincipalIso" value={empresa.monedaPrincipalIso} disabled={!isEditing} onChange={(e) => handleInputChange(e, 'monedaPrincipalIso')} placeholder="Ej: PEN" />
                                                </span>
                                            </div>
                                            <div className="col-12 md:col-4 field mb-4">
                                                <label htmlFor="monedaSimbolo" className="font-medium text-700">Símbolo Moneda Base <span className="text-red-500">*</span></label>
                                                <span className="p-input-icon-left w-full">
                                                    <i className="pi pi-money-bill" />
                                                    <InputText style={{ paddingLeft: "2.5rem" }} id="monedaSimbolo" value={empresa.monedaSimbolo} disabled={!isEditing} onChange={(e) => handleInputChange(e, 'monedaSimbolo')} placeholder="Ej: S/" />
                                                </span>
                                            </div>
                                            <div className="col-12 md:col-4 field mb-0">
                                                <label htmlFor="valorImpuesto" className="font-medium text-700">Impuesto Base (%) <span className="text-red-500">*</span></label>
                                                <div className="p-inputgroup">
                                                    <InputText id="valorImpuesto" type="number" value={empresa.valorImpuesto} disabled={!isEditing} onChange={(e) => handleInputChange(e, 'valorImpuesto')} placeholder="Ej: 18" />
                                                    <span className="p-inputgroup-addon">%</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </TabPanel>

                        <TabPanel header="Tienda (E-Commerce)" leftIcon="pi pi-shop text-blue-500 mr-2">
                            <div className="grid">
                                <div className="col-12 lg:col-6">
                                    <div className="surface-card p-4 shadow-2 border-round-xl h-full line-height-3">
                                        <div className="text-xl font-medium text-900 mb-4 border-bottom-1 surface-border pb-3">
                                            <i className="pi pi-box text-primary mr-2"></i>
                                            Configuración de Inventario Público
                                        </div>

                                        <p className="text-600 mb-5">
                                            Seleccione qué tipo de stock será visible para los clientes en su página de E-Commerce (Catálogo de Cotización).
                                        </p>

                                        <div className="p-fluid">
                                            <div className="field">
                                                <label htmlFor="tipoStockTienda" className="font-medium text-700">Filtrado de Stock <span className="text-red-500">*</span></label>
                                                <Dropdown
                                                    id="tipoStockTienda"
                                                    value={empresa.tipoStockTienda}
                                                    options={tipoStockOptions}
                                                    disabled={!isEditing}
                                                    onChange={(e) => handleInputChange(e, 'tipoStockTienda')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </TabPanel>
                    </TabView>

                    <div className="flex justify-content-end align-items-center gap-3 mt-4 pt-4 border-top-1 surface-border">

                        {!isEditing ? (
                            <Button label="Modificar Datos" icon="pi pi-pencil" onClick={handleEdit} className="p-button-primary px-4 shadow-1 disabled-state-opacity" />
                        ) : (
                            <>
                                <Button label="Cancelar" icon="pi pi-times" onClick={handleCancel} className="p-button-text p-button-secondary px-4 disabled-state-opacity" disabled={isLoading} />
                                <Button label={isLoading ? 'Guardando...' : 'Guardar Cambios'} icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-save"} onClick={saveChanges} className="p-button-success px-4 shadow-1 disabled-state-opacity" disabled={isLoading} />
                            </>
                        )}

                    </div>

                </Card>
            </div>
        </div>
    );
};

export default Empresa;
